// KOA-5857 — Image / Signature cell editor.
//
// The generic BinaryCellEditor is right for opaque bytes (a file
// download blob, a binary BLOB column the user can't read) — a chip
// that says "binary · 4.2 KB" and a file picker. But for `Image` and
// `Signature` columns we know the bytes are pixels, and a database
// tool that hides them behind a chip is doing the wrong thing.
//
// This component renders the actual image:
//
//   [thumb] 240×180 · 4.2 KB  [Replace ×]
//
// - Thumb is the actual decoded image at 24 px. Click → lightbox.
// - Dimensions + decoded size are computed from the in-memory image
//   and the base64 payload respectively; neither hits the network.
// - File picker is `accept="image/*"` so the OS dialog filters to
//   image files only.
// - If the bytes fail to decode as an image we fall back to the
//   "broken image" mark + size chip so the user can still see the
//   value exists and can be replaced / cleared.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import KuikaFileInput from "../ui-kit/kuika-file-input/kuika-file-input";
import {
  approxBase64Bytes,
  detectImageMime,
  downloadBase64,
  extensionForMime,
  extractDataUriMime,
  formatByteSize,
  isDataUri,
  readFileAsDataUri,
  sanitiseFileNamePart,
  shortRowKeyForFilename,
  sniffImageFile,
  stripDataUriPrefix
} from "./binary-cell-helpers";
import DownloadFilenamePopover from "./download-filename-popover";

// 25 MB is the inline cap for image cells. Decoded base64 lands at
// ~33 MB which the grid can shuffle around without stalling; bigger
// images belong in dedicated upload flows. Lower than the generic
// binary cap (50 MB) since image dimensions tend to mean someone
// dragged in a multi-megapixel raw and almost never *meant* to.
const MAX_IMAGE_BYTES: number = 25 * 1024 * 1024;

export interface IImageCellEditorProps {
  value: unknown;
  // "Image" | "Signature" — controls the empty-state label only.
  dataType: string;
  // Column name — used to suggest a filename when the user downloads
  // the image. Falls back to `image` / `signature` if missing.
  columnName?: string | null;
  // Row's primary-key value — appended to the download filename so
  // multiple rows of the same table don't all download as
  // `<column>.<ext>`. See `shortRowKeyForFilename` for the
  // GUID-head / numeric / truncated-string rules.
  rowKey?: string | null;
  disabled?: boolean;
  onChange: (next: unknown) => void;
  onBlur?: () => void;
}

interface IDimensions {
  width: number;
  height: number;
}

const ImageCellEditor: React.FC<IImageCellEditorProps> = (props: IImageCellEditorProps): JSX.Element => {
  const { value, dataType, columnName, rowKey, disabled, onChange, onBlur } = props;
  const raw: string = typeof value === "string" ? value : "";
  const hasValue: boolean = raw.length > 0;

  // Decode the storage shape once. Two formats co-exist:
  //   - Full data URI (what new picks store + what runtime widgets
  //     write): `data:image/png;base64,iVBO…`. The MIME is right
  //     there in the prefix.
  //   - Body-only base64 (legacy table-editor format + a few
  //     pre-existing rows): `iVBO…`. We sniff MIME from the magic
  //     bytes via `detectImageMime`.
  //
  // `body` is always the pure base64 (used for size / download).
  // `imageSrc` is what feeds <img src> — the original raw value
  // when it's already a data URI, or one we reconstruct from the
  // body + sniffed MIME.
  const body: string = useMemo((): string => stripDataUriPrefix(raw), [raw]);
  const mime: string = useMemo((): string => {
    if (!hasValue) {
      return "image/png";
    }
    return extractDataUriMime(raw) ?? detectImageMime(body);
  }, [hasValue, raw, body]);
  const imageSrc: string | null = useMemo((): string | null => {
    if (!hasValue) {
      return null;
    }
    if (isDataUri(raw)) {
      return raw;
    }
    return `data:${mime};base64,${body}`;
  }, [hasValue, raw, body, mime]);

  const downloadFilename: string = useMemo((): string => {
    const base: string =
      typeof columnName === "string" && columnName.length > 0
        ? sanitiseFileNamePart(columnName)
        : dataType === "Signature"
          ? "signature"
          : "image";
    // Suffix with a short row-key fragment so the same column from
    // different rows produces distinct filenames instead of
    // stacking as `(1)`, `(2)`.
    const rowKeyShort: string = shortRowKeyForFilename(rowKey);
    const ext: string = extensionForMime(mime);
    return rowKeyShort.length > 0 ? `${base}-${rowKeyShort}.${ext}` : `${base}.${ext}`;
  }, [columnName, dataType, mime, rowKey]);

  const handleDownloadConfirm = (filename: string): void => {
    if (body.length === 0) {
      return;
    }
    const outcome = downloadBase64(body, mime, filename);
    if (!outcome.ok) {
      // Surface via the same pickError chip the upload-validation path
      // uses — keeps the failure mode visible in the cell instead of
      // swallowing it.
      setPickError(outcome.errorMessage ?? "Couldn't download this image.");
      return;
    }
    setPickError(null);
    setDownloadPopoverAnchor(null);
  };

  // Pick handler with real validation. `accept="image/*"` and
  // `file.type` are only OS-level hints — a renamed binary keeps
  // both happy. The two guards below fire on the actual File we
  // got:
  //
  //   1. Size <= MAX_IMAGE_BYTES — checked first because it lets
  //      us fail fast on huge picks before we read any bytes.
  //   2. Content sniff (`sniffImageFile`) — reads the first 16
  //      bytes via `file.slice(0,16).arrayBuffer()` and matches
  //      against PNG / JPEG / GIF / WebP / BMP magic-byte headers.
  //      SVG (text) gets a separate first-512-chars text read with
  //      an anchored `<svg` / `<?xml ... <svg` check. A renamed
  //      non-image is rejected here even though the filename and
  //      `file.type` looked fine.
  //
  // Errors surface as an inline chip; the existing value is left
  // intact so the user can recover.
  //
  // Storage format: full `data:<mime>;base64,…` URI so the runtime
  // `Image` / `Signature` widgets can drop it straight into
  // `<img src>` / `signaturePad.fromDataURL`. Body-only storage
  // was the previous bug — runtime saw raw base64 in `<img src>`
  // and rendered nothing.
  const handlePick = async (file: File | null): Promise<void> => {
    setPickError(null);
    if (file === null) {
      onChange(null);
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setPickError(`${formatByteSize(file.size)} exceeds the ${formatByteSize(MAX_IMAGE_BYTES)} inline limit.`);
      return;
    }
    setBusy(true);
    try {
      const sniffed: string | null = await sniffImageFile(file);
      if (sniffed === null) {
        setPickError(`“${file.name || "this file"}” doesn't have a recognised image header.`);
        return;
      }
      const dataUri: string = await readFileAsDataUri(file);
      onChange(dataUri);
    } catch {
      setPickError("Couldn't read this file.");
    } finally {
      setBusy(false);
    }
  };

  const [dimensions, setDimensions] = useState<IDimensions | null>(null);
  const [loadFailed, setLoadFailed] = useState<boolean>(false);
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [pickError, setPickError] = useState<string | null>(null);
  const [busy, setBusy] = useState<boolean>(false);
  // Hover popover — small floating preview near the cell. Anchored
  // via a cached DOMRect so the portal can sit at viewport coords
  // without being clipped by the table's scroll container.
  const thumbRef = useRef<HTMLButtonElement | null>(null);
  const hoverTimerRef = useRef<number | null>(null);
  const [hoverRect, setHoverRect] = useState<DOMRect | null>(null);
  // Modal focus management. lightboxRef wraps the dialog so we can
  // query its focusable children for the Tab trap. focusReturnRef
  // remembers the element that owned focus before the lightbox opened
  // (almost always the thumb button) so we can restore it on close.
  const lightboxRef = useRef<HTMLDivElement | null>(null);
  const lightboxCloseRef = useRef<HTMLButtonElement | null>(null);
  const focusReturnRef = useRef<HTMLElement | null>(null);
  // Download popover — tracks which trigger opened it so the popover
  // anchors against the right element (cell `↓` vs lightbox `↓`).
  const cellDownloadBtnRef = useRef<HTMLButtonElement | null>(null);
  const lightboxDownloadBtnRef = useRef<HTMLButtonElement | null>(null);
  const [downloadPopoverAnchor, setDownloadPopoverAnchor] = useState<HTMLElement | null>(null);

  // Decode-side-effect — runs once per data URI. Uses the cancelled
  // flag so a rapid replace doesn't race a stale `onload` against a
  // newer one.
  useEffect((): (() => void) => {
    if (imageSrc === null) {
      setDimensions(null);
      setLoadFailed(false);
      return (): void => {};
    }
    let cancelled: boolean = false;
    setDimensions(null);
    setLoadFailed(false);
    const probe: HTMLImageElement = new Image();
    probe.onload = (): void => {
      if (cancelled) {
        return;
      }
      setDimensions({ width: probe.naturalWidth, height: probe.naturalHeight });
    };
    probe.onerror = (): void => {
      if (cancelled) {
        return;
      }
      setLoadFailed(true);
    };
    probe.src = imageSrc;
    return (): void => {
      cancelled = true;
    };
  }, [imageSrc]);

  // Esc closes the lightbox. Listener only attached while the
  // lightbox is open so we don't burn keydown handlers on idle cells.
  useEffect((): (() => void) => {
    if (!previewOpen) {
      return (): void => {};
    }
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        setPreviewOpen(false);
      }
    };
    document.addEventListener("keydown", handleKey);
    return (): void => document.removeEventListener("keydown", handleKey);
  }, [previewOpen]);

  // Focus management — what makes the lightbox an actual modal
  // instead of a styled overlay. Three concerns:
  //
  //   1. Initial focus moves to the Close button so keyboard users
  //      can dismiss with Enter / Space immediately.
  //   2. Focus restoration: when the lightbox closes, focus returns
  //      to whatever owned it on open (typically the thumb), so a
  //      keyboard user keeps their place in the grid.
  //   3. Focus trap: Tab from the last action wraps to the first,
  //      and Shift-Tab from the first wraps to the last. Without
  //      this, Tab leaves the dialog and lands on background
  //      content the user can't see — the canonical accessibility
  //      bug for modal overlays.
  useEffect((): (() => void) => {
    if (!previewOpen) {
      return (): void => {};
    }
    const active: Element | null = document.activeElement;
    focusReturnRef.current = active instanceof HTMLElement ? active : null;
    // Defer to the next tick so the close button is mounted before
    // we try to focus it.
    const focusFrame: number = window.requestAnimationFrame((): void => {
      lightboxCloseRef.current?.focus();
    });
    const handleTrap = (e: KeyboardEvent): void => {
      if (e.key !== "Tab") {
        return;
      }
      const root: HTMLDivElement | null = lightboxRef.current;
      if (root === null) {
        return;
      }
      const focusable: NodeListOf<HTMLElement> = root.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) {
        return;
      }
      const first: HTMLElement = focusable[0]!;
      const last: HTMLElement = focusable[focusable.length - 1]!;
      // Tab from last → first; Shift-Tab from first → last.
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleTrap);
    return (): void => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", handleTrap);
      // Restore focus to whatever had it before we opened. Guard
      // against the node having been removed from the DOM (e.g. row
      // virtualised away while the lightbox was open).
      const target: HTMLElement | null = focusReturnRef.current;
      focusReturnRef.current = null;
      if (target !== null && document.body.contains(target)) {
        target.focus();
      }
    };
  }, [previewOpen]);

  // Hover-popover dismissal — Esc, scroll, or a click anywhere kills
  // it. Scroll listener uses `passive: true` so it doesn't fight the
  // table's natural scroll perf.
  useEffect((): (() => void) => {
    if (hoverRect === null) {
      return (): void => {};
    }
    const dismiss = (): void => setHoverRect(null);
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        setHoverRect(null);
      }
    };
    document.addEventListener("keydown", handleKey);
    window.addEventListener("scroll", dismiss, true);
    return (): void => {
      document.removeEventListener("keydown", handleKey);
      window.removeEventListener("scroll", dismiss, true);
    };
  }, [hoverRect]);

  // Clean up any pending hover-show timer when unmounting so we don't
  // call setState on a dead component (e.g. row got virtualised away
  // while the cursor was settling on the thumb).
  useEffect((): (() => void) => {
    return (): void => {
      if (hoverTimerRef.current !== null) {
        window.clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
    };
  }, []);

  const scheduleHover = (): void => {
    if (hoverTimerRef.current !== null) {
      window.clearTimeout(hoverTimerRef.current);
    }
    // ~180 ms beat — long enough not to flicker while sweeping the
    // cursor across rows, short enough that intentional hovers feel
    // instant.
    hoverTimerRef.current = window.setTimeout((): void => {
      hoverTimerRef.current = null;
      const node: HTMLButtonElement | null = thumbRef.current;
      if (node === null) {
        return;
      }
      setHoverRect(node.getBoundingClientRect());
    }, 180);
  };

  const cancelHover = (): void => {
    if (hoverTimerRef.current !== null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setHoverRect(null);
  };

  // Position the hover popover. Lives above the empty-cell early
  // return so the hook count stays identical between the empty and
  // filled branches — React's Rules of Hooks require it.
  const popoverStyle: React.CSSProperties | null = useMemo((): React.CSSProperties | null => {
    if (hoverRect === null) {
      return null;
    }
    const PREVIEW_MAX: number = 240;
    const GAP: number = 8;
    const VIEWPORT_PAD: number = 16;
    const vw: number = window.innerWidth;
    const vh: number = window.innerHeight;
    const spaceBelow: number = vh - hoverRect.bottom;
    const showAbove: boolean = spaceBelow < PREVIEW_MAX + GAP + VIEWPORT_PAD;
    const top: number = showAbove ? hoverRect.top - GAP - PREVIEW_MAX : hoverRect.bottom + GAP;
    let left: number = hoverRect.left;
    if (left + PREVIEW_MAX > vw - VIEWPORT_PAD) {
      left = Math.max(VIEWPORT_PAD, vw - VIEWPORT_PAD - PREVIEW_MAX);
    }
    return {
      top,
      left,
      maxWidth: PREVIEW_MAX,
      maxHeight: PREVIEW_MAX
    };
  }, [hoverRect]);

  // ── Empty cell — single image-only picker ────────────────────────
  if (!hasValue) {
    return (
      <span className="kuika-image-cell kuika-image-cell--empty">
        <KuikaFileInput
          variant="cell"
          size="sm"
          accept="image/*"
          busy={busy}
          emptyLabel={dataType === "Signature" ? "Upload signature…" : "Upload image…"}
          disabled={disabled}
          onChange={(file: File | null): void => {
            void handlePick(file);
          }}
          onBlur={onBlur}
        />
        {pickError !== null && (
          <span className="kuika-image-cell__error" role="alert">
            {pickError}
          </span>
        )}
      </span>
    );
  }

  const sizeText: string = formatByteSize(approxBase64Bytes(body));
  const dimsText: string | null = dimensions !== null ? `${dimensions.width}×${dimensions.height}` : null;
  // Show dims+size when decode succeeded; fall back to just the size
  // (prefixed with a generic "image" word so the chip still reads).
  const summaryText: string = dimsText !== null ? `${dimsText} · ${sizeText}` : `image · ${sizeText}`;

  const canPreview: boolean = !loadFailed && imageSrc !== null;

  return (
    <span className="kuika-image-cell">
      {canPreview ? (
        <button
          ref={thumbRef}
          type="button"
          className="kuika-image-cell__thumb"
          onClick={(): void => {
            cancelHover();
            setPreviewOpen(true);
          }}
          onMouseEnter={scheduleHover}
          onMouseLeave={cancelHover}
          onFocus={scheduleHover}
          onBlur={cancelHover}
          aria-label="Preview image"
          title="Preview"
          disabled={disabled}
        >
          <img src={imageSrc ?? ""} alt="" className="kuika-image-cell__thumb-img" draggable={false} />
        </button>
      ) : (
        <span
          className="kuika-image-cell__thumb kuika-image-cell__thumb--broken"
          aria-label="Image data is not a recognised format"
          title="Not a valid image"
        >
          ⃠
        </span>
      )}
      <span className="kuika-image-cell__meta" title={summaryText}>
        {summaryText}
      </span>
      <button
        ref={cellDownloadBtnRef}
        type="button"
        className="kuika-image-cell__icon-btn"
        onClick={(): void => {
          setDownloadPopoverAnchor((prev: HTMLElement | null): HTMLElement | null =>
            prev === cellDownloadBtnRef.current ? null : cellDownloadBtnRef.current
          );
        }}
        aria-label="Download image"
        aria-haspopup="dialog"
        aria-expanded={downloadPopoverAnchor === cellDownloadBtnRef.current}
        title="Download"
        disabled={disabled}
      >
        ↓
      </button>
      <KuikaFileInput
        variant="cell"
        size="sm"
        accept="image/*"
        busy={busy}
        valueLabel="Replace"
        showClear={true}
        disabled={disabled}
        className="kuika-image-cell__file"
        onChange={(file: File | null): void => {
          void handlePick(file);
        }}
        onBlur={onBlur}
      />
      {pickError !== null && (
        <span className="kuika-image-cell__error" role="alert">
          {pickError}
        </span>
      )}

      {previewOpen &&
        canPreview &&
        createPortal(
          <div
            ref={lightboxRef}
            className="kuika-image-cell__lightbox"
            role="dialog"
            aria-modal="true"
            aria-label="Image preview"
            onClick={(): void => setPreviewOpen(false)}
          >
            <div className="kuika-image-cell__lightbox-actions">
              <button
                ref={lightboxDownloadBtnRef}
                type="button"
                className="kuika-image-cell__lightbox-action"
                aria-label="Download image"
                aria-haspopup="dialog"
                aria-expanded={downloadPopoverAnchor === lightboxDownloadBtnRef.current}
                title="Download"
                onClick={(e: React.MouseEvent<HTMLButtonElement>): void => {
                  e.stopPropagation();
                  setDownloadPopoverAnchor((prev: HTMLElement | null): HTMLElement | null =>
                    prev === lightboxDownloadBtnRef.current ? null : lightboxDownloadBtnRef.current
                  );
                }}
              >
                ↓
              </button>
              <button
                ref={lightboxCloseRef}
                type="button"
                className="kuika-image-cell__lightbox-action"
                aria-label="Close preview"
                title="Close"
                onClick={(e: React.MouseEvent<HTMLButtonElement>): void => {
                  e.stopPropagation();
                  setPreviewOpen(false);
                }}
              >
                ×
              </button>
            </div>
            <figure
              className="kuika-image-cell__lightbox-figure"
              onClick={(e: React.MouseEvent<HTMLElement>): void => e.stopPropagation()}
            >
              <img src={imageSrc ?? ""} alt="" className="kuika-image-cell__lightbox-img" />
              <figcaption className="kuika-image-cell__lightbox-caption">{summaryText}</figcaption>
            </figure>
          </div>,
          document.body
        )}

      {hoverRect !== null &&
        !previewOpen &&
        canPreview &&
        popoverStyle !== null &&
        createPortal(
          <div className="kuika-image-cell__hover" style={popoverStyle} role="presentation" aria-hidden="true">
            <img src={imageSrc ?? ""} alt="" className="kuika-image-cell__hover-img" draggable={false} />
            <span className="kuika-image-cell__hover-caption">{summaryText}</span>
          </div>,
          document.body
        )}

      {downloadPopoverAnchor !== null && (
        <DownloadFilenamePopover
          anchor={downloadPopoverAnchor}
          defaultFilename={downloadFilename}
          onConfirm={handleDownloadConfirm}
          onClose={(): void => setDownloadPopoverAnchor(null)}
        />
      )}
    </span>
  );
};

export default ImageCellEditor;
